import DivLoader from "components/divloader/DivLoader";
import { useRef, useState } from "react";
import toast from 'react-hot-toast';
import { customerRegistrationFormFront } from "api/graphQL_customers";
import {
  Button,
} from "@chakra-ui/react";
import InputField from "components/fields/InputField";
import moment from "moment";
import Card from "components/card/custom";
import { BsFileText } from "react-icons/bs";
import { Select as AntSelect } from "antd";
import { MdCreditCard, MdPeople, MdVerifiedUser } from "react-icons/md";

interface IGeneral {
    first_name: string;
    middle_name?: string;
    last_name: string;
    email: string;
    phone: string;
    city: string;
    address: string;
    postal_code: string;
    state: string;
    country: string;
    ip: string;
    birth_date?: string;
    reference_id?:string;
}

interface IKyc {
    identification_number?: string;
    identification_type?: string;
    identification_issued_at?: string;
    identification_expired_at?: string;
    document_file?: string;
    fileKey? : string;
    document_file_name?: string,
    document_file_type?: string 
}

interface ICard {
    cardholder_name: string;
    card_number: string;
    expiration_month: string;
    expiration_year: string;
    cvv: string;
}

interface IBank {
    iban: string;
    swift_no: string;
    bank_id: string;
    account_number: string;
    account_type: string;
    bank_name: string;
    bank_branch: string;
}

interface ICrypto {
    wallet_address: string;
}

interface IPaymentMethod {
    type: "bank" | "crypto" | "card";
    card?: ICard[];
    bank?: IBank[];
    crypto?: ICrypto[];
}

interface ICreateCustomerForm {
    client_id: string;
    general: IGeneral;
    kyc: IKyc[];
    payment_method: IPaymentMethod;
}

function CustomerRegistrationForm({
  onClose,
  clients,
  fetchClients
}: {
  onClose: () => void;
  clients : any;
  fetchClients : () => void;
}) {

    const rootForm: ICreateCustomerForm = {
        client_id: "",
        general: {
            first_name: "",
            middle_name: "",
            last_name: "",
            email: "",
            phone: "",
            city: "",
            address: "",
            postal_code: "",
            state: "",
            country: "",
            ip: "",
            birth_date: "",
            reference_id : ""
        },
        kyc: [
            {
                identification_number: "",
                identification_type: "",
                identification_issued_at: "",
                identification_expired_at: "",
                document_file: "",
                fileKey : Math.random().toString(36),
                document_file_name: "",
                document_file_type: "", 
            }
        ],
        payment_method: {
            type: "card",
            card:[ 
                {
                    cardholder_name: "",
                    card_number: "",
                    expiration_month: "",
                    expiration_year: "",
                    cvv: ""
                }
            ],
            bank: [
                {
                    iban: "",
                    swift_no: "",
                    bank_id: "",
                    account_number: "",
                    account_type: "",
                    bank_name: "",
                    bank_branch: ""
                }
            ],
            crypto: [
                { wallet_address: "" }
            ]
        }
    };
    
    const [isLoading, setIsLoading] = useState<boolean>();
    const [formValues, setFormValues] = useState<ICreateCustomerForm>(rootForm);
    const [formValuesErr, setFormValuesErr] =useState<ICreateCustomerForm>(rootForm);
    const [step, setStep] = useState(1);
    const steps = [
        { id: 1, label: "Choose Client", icon: MdPeople },
        { id: 2, label: "General Information", icon: MdVerifiedUser },
        { id: 3, label: "KYC Information", icon: BsFileText },
        { id: 4, label: "Payment Methods", icon: MdCreditCard },
    ];

    const fileRefs = useRef<any>({});

    const nextStep = () => {
        if( step  === 1) {
            const validationErrors = validateData(formValues);
            setFormValuesErr(validationErrors as ICreateCustomerForm);
            const hasErrors = Object.values(validationErrors).some(err => Boolean(err));
            if (!hasErrors) {
                setStep((prev) => Math.min(prev + 1, steps.length));
            }
        } else if( step  === 2) {
            const validationErrors = validateForm(formValues);
            setFormValuesErr(validationErrors as ICreateCustomerForm);
            const hasErrors = Object.values(validationErrors.general || {}).some(
                (err) => Boolean(err)
            );
            if (!hasErrors) {
                setStep((prev) => Math.min(prev + 1, steps.length));
            }
        } else if(step === 3) {
            const validationErrors = validateFormKyc(formValues);
            setFormValuesErr(validationErrors as ICreateCustomerForm);
            const hasErrors = JSON.stringify(validationErrors).includes("The field is required");
            if (!hasErrors) {
                setStep((prev) => Math.min(prev + 1, steps.length))
            }
        }
    }

    const prevStep = () => setStep((prev) => Math.max(prev - 1, 1));
   
    const validateForm = (values: ICreateCustomerForm) => {
        const errors: Partial<ICreateCustomerForm> = {
            general: {} as IGeneral
        };
        const optionalFields = ["birth_date", "ip", "middle_name", "reference_id", "callback_url", "return_url"];
        const isValidEmail = (email: string): boolean =>
          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);
        for (const key in values.general) {
            const value = values.general[key as keyof IGeneral];
            if (!value && !optionalFields.includes(key)) {
                (errors.general as any)[key] = "The field is required";
                continue;
            }
            if (key === "email" && value) {
                if (!isValidEmail(value as string)) {
                    (errors.general as any)[key] = "Invalid email format";
                }
            }
        }
        return errors;
    };

    const validateData = (values: ICreateCustomerForm) => {
        const errors: Partial<ICreateCustomerForm> = {
            client_id: "",
        };
        if(values.client_id == "" || values.client_id == undefined || values.client_id == null) {
            errors.client_id = "The field is required"
        } else {
            errors.client_id = ""
        }
        return errors;
    };

    const validateFormKyc = (values: ICreateCustomerForm) => {
        const errors: any = { kyc: [] };
        values.kyc.forEach((item, index) => {
            const kycErrors: any = {};
            const num    = item.identification_number?.trim();
            const type   = item.identification_type?.trim();
            const issued = item.identification_issued_at?.trim();
            const expire = item.identification_expired_at?.trim();
            const document = item.document_file?.trim();

            if (!num && !type) {
                kycErrors.identification_number = "The field is required";
                kycErrors.identification_type = "The field is required";
            }
            if (num && !type) {
                kycErrors.identification_type = "The field is required";
            }
            if (!num && type) {
                kycErrors.identification_number = "The field is required";
            }
            if (!issued && !expire) {
            }
            if (issued && !expire) {
                kycErrors.identification_expired_at = "The field is required";
            }
            if (expire && !issued) {
                kycErrors.identification_issued_at = "The field is required";
            }
            if (!document) {
                kycErrors.document_file = "The field is required";
            }
            errors.kyc[index] = kycErrors;
        });
        return errors;
    };

    const validateFormPayment = (values: ICreateCustomerForm) => {
        const errors: Partial<ICreateCustomerForm> = {
            payment_method: { type: values.payment_method.type } as IPaymentMethod,
        };
        const pm = values.payment_method;
        if (pm.type === "card") {
            if (pm.card && Array.isArray(pm.card)) {
                errors.payment_method.card = [];
                pm.card.forEach((cardItem, index) => {
                    const cardErrors: any = {};
                    const fields: (keyof ICard)[] = [
                        "cardholder_name",
                        "card_number",
                        "expiration_month",
                        "expiration_year",
                        "cvv"
                    ];
                    fields.forEach((field) => {
                        const value = cardItem[field]?.toString().trim();
                        if (!value) {
                            cardErrors[field] = "The field is required";
                        }
                    });
                    (errors.payment_method.card as any)[index] = cardErrors;
                });
            }
        }
        if (pm.type === "crypto") {
            if (pm.crypto && Array.isArray(pm.crypto)) {
                errors.payment_method.crypto = [];
                pm.crypto.forEach((cryptoItem, index) => {
                    const cryptoErrors: any = {};
                    if (!cryptoItem.wallet_address?.trim()) {
                        cryptoErrors.wallet_address = "The field is required";
                    }
                    (errors.payment_method.crypto as any)[index] = cryptoErrors;
                });
            }
        }
        if (pm.type === "bank") {
            if (pm.bank && Array.isArray(pm.bank)) {
                errors.payment_method.bank = [];
                pm.bank.forEach((bankItem, index) => {
                    const bankErrors: any = {};
                    const requiredFields: (keyof IBank)[] = [
                        "iban",
                        "swift_no"
                    ];
                    requiredFields.forEach((field) => {
                        const value = bankItem[field]?.toString().trim();
                        if (!value) {
                            bankErrors[field] = "The field is required";
                        }
                    });
                    (errors.payment_method.bank as any)[index] = bankErrors;
                });
            }
        }
        return errors;
    };
    
    const handleSubmit = async(e: any) => {
        e.preventDefault();
        const validationErrors = validateFormPayment(formValues);
        setFormValuesErr(validationErrors as ICreateCustomerForm);
        const hasErrors = JSON.stringify(validationErrors).includes("The field is required");
        if (!hasErrors) {
            setIsLoading(true);
            const create = await customerRegistrationFormFront(formValues)
            if (create.status == "Error") {
                toast.error(create.message ?? "Error in creating customer");
                setIsLoading(false);
            } else {
                if(create.status) {
                    toast.success(create.message ?? "Customer created successfully");
                    onClose();
                    fetchClients();
                    setIsLoading(false);
                } else {
                    toast.error(create.message ?? "Error in creating customer");
                    setIsLoading(false);
                }
            }
        } 
    }

    const addKyc = () => {
        setFormValues((prevVal: any) => ({
            ...prevVal,
            kyc: [
                ...prevVal.kyc,
                {
                    identification_number: "",
                    identification_type: "",
                    identification_issued_at: "",
                    identification_expired_at: "",
                    document_file : "",
                    fileKey : crypto.randomUUID(),
                    document_file_name: "",
                    document_file_type: "", 
                }
            ]
        }));
    };

    // const removeKyc = (index: number) => {
    //     setFormValues((prevVal: any) => ({
    //         ...prevVal,
    //         kyc: prevVal.kyc.filter((_: any, i: number) => i !== index)
    //     }));
    // };

    const removeKyc = (index: number) => {
        setFormValues((prevVal: any) => ({
            ...prevVal,
            kyc: prevVal.kyc.filter((_: any, i: number) => i !== index),
        }));
        setFormValuesErr((prevErr: any) => {
            if (!prevErr?.kyc) return prevErr;
            const updatedKycErr = prevErr.kyc.filter((_: any, i: number) => i !== index);
            if (updatedKycErr.length === 0) {
                const { kyc, ...rest } = prevErr;
                return rest;
            }
            return { ...prevErr, kyc: updatedKycErr };
        });
    };


    const compressImage = (file: File, maxWidth = 1280, quality = 0.6): Promise<string> => {
        return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement("canvas");
                let width = img.width;
                let height = img.height;
                if (width > maxWidth) {
                    height = (maxWidth / width) * height;
                    width = maxWidth;
                }
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext("2d")!;
                ctx.drawImage(img, 0, 0, width, height);
                const compressedBase64 = canvas.toDataURL("image/jpeg", quality);
                resolve(compressedBase64);
            };
            img.src = URL.createObjectURL(file);
        });
    };

    const MAX_SIZE_MB = 2;
    const MAX_FILE_BYTES = MAX_SIZE_MB * 1024 * 1024;

    const handleFileUpload = async (e: any, fileKey: string, index: number) => {
        const file = e.target.files[0];
        if (!file) return;
        if (file.size > MAX_FILE_BYTES) {
            toast.error(`File too large. Max allowed size is ${MAX_SIZE_MB} MB.`);
            return;
        }
        let base64 = "";
        let fileType = "";
        if (file.type.startsWith("image/")) {
            base64 = await compressImage(file);
            fileType = "image";
        }  else if (file.type === "application/pdf") {
            fileType = "pdf";
            const reader = new FileReader();
            base64 = await new Promise(resolve => {
                reader.onloadend = () => resolve(reader.result as string);
                reader.readAsDataURL(file);
            });
        }  else {
            toast.error("Only image and PDF files are allowed.");
            return;
        }
        setFormValues(prev => {
            const updated = [...prev.kyc];
            updated[index].document_file = base64;
            updated[index].document_file_type = fileType;
            updated[index].document_file_name = file.name;
            return { ...prev, kyc: updated };
        });
        setFormValuesErr(prevErr => {
            const updated = { ...prevErr };
            if (!updated.kyc) updated.kyc = [];
            if (!updated.kyc[index]) updated.kyc[index] = {};
            delete updated.kyc[index].document_file;
            return updated;
        });
    };

    return (
        <>
            <div className="flex flex-col lg:flex-row  mx-auto">
                <div className="w-full  px-8 ">
                    <div className="w-full mx-auto">
                        <div className="relative flex items-center w-full mb-10">
                            {steps.map((s, index) => {
                            const Icon = s.icon;
                            const isActive = step > index;
                            return (
                                <div key={s.id} className={`flex items-center ${index < steps.length - 1 ? 'flex-1' : ''}`}>
                                <div className="relative flex flex-col items-center z-10">
                                    <div
                                    className={`w-14 h-14 ${isActive ? "bg-brand-500 text-white" : "bg-[#D1D5DB] text-[#8f7c7c]"} flex items-center justify-center rounded-full shadow-lg transition-all duration-500 border-2 border-current`}
                                    >
                                    <Icon size={26} />
                                    </div>
                                    <p
                                    className={`mt-3 text-sm font-semibold transition-all duration-300`}
                                    >
                                    {s.label}
                                    </p>
                                </div>
                                {index < steps.length - 1 && (
                                    <div className={`h-1 flex-1 mx-3 transition-all duration-500 rounded-full ${step > index + 1 ? "bg-brand-500" : "bg-[#D1D5DB]"}`}/>
                                )}
                                </div>
                            );
                            })}
                        </div>
                        <Card className="backdrop-blur-xl bg-white/60 shadow-md rounded-3xl border border-gray-200">
                            <div className="p-8">
                                {step === 1 && (
                                        <div className="animate-fadeIn">
                                            <h2 className="text-xl font-semibold mb-4 text-gray-800">Choose Client</h2>
                                            <div className="flex gap-5">
                                                <div className="w-full">
                                                    <label
                                                        htmlFor="org_id"
                                                        className={`ml-1.5 text-sm font-bold text-gray-900 dark:text-white`}
                                                    >
                                                        Client *
                                                    </label>
                                                    <AntSelect
                                                        showSearch={true}
                                                        filterOption={(input, option) =>
                                                            String(option?.label).toLowerCase().includes(input.toLowerCase())
                                                        }
                                                        allowClear
                                                        placeholder="Select Clients"
                                                        optionFilterProp="children"
                                                        className="h-[48px] w-full mt-[5px]"
                                                        options={clients?.map((m: any) => {
                                                        return {
                                                            label: m?.name,
                                                            value: m?.id,
                                                        };
                                                        })}
                                                        value={formValues?.client_id || undefined}
                                                        onChange={(e: any) => {
                                                            setFormValues((preVal: any) => {
                                                                return { ...preVal, client_id: e };
                                                            });
                                                            setFormValuesErr((preVal: any) => ({
                                                                ...preVal,
                                                                client_id: e ? "" : "The field is required",
                                                            }));
                                                        }}
                                                        notFoundContent={"Entered client not found!"}
                                                    />
                                                    {formValuesErr?.client_id && (
                                                        <p className="text-sm text-orange-500">
                                                        The field is required
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                )}
                                {step === 2 && (
                                    <div className="animate-fadeIn">
                                        <h2 className="text-xl font-semibold mb-4 text-gray-800">General Information</h2>
                                            <div className="flex gap-5">
                                                <InputField
                                                variant="auth"
                                                extra="mb-2 w-full"
                                                label="First Name *"
                                                placeholder="Enter Value"
                                                id="first_name"
                                                type="text"
                                                value={formValues?.general.first_name}
                                                onChange={(e: any) => {
                                                    const value = e.target.value
                                                    setFormValues((prevVal: any) => ({
                                                        ...prevVal,
                                                        general: {
                                                            ...prevVal.general,
                                                            first_name: value
                                                        },
                                                    }));
                                                    setFormValuesErr((prevErr: any) => {
                                                        const newErrors = { ...prevErr };
                                                        if (!newErrors.general) {
                                                            newErrors.general = {};
                                                        }
                                                        if (!newErrors.general.first_name) {
                                                            newErrors.general.first_name = {};
                                                        }
                                                        if (!value) {
                                                            newErrors.general.first_name = "The field is required";
                                                        } else {
                                                            delete newErrors.general.first_name;
                                                            if (Object.keys(newErrors.general).length === 0) {
                                                                delete newErrors.general;
                                                            }
                                                        }
                                                        return newErrors;
                                                    });
                                                }}
                                                errMessage={formValuesErr?.general?.first_name || ""}
                                                />
                                                <InputField
                                                variant="auth"
                                                extra="mb-2 w-full"
                                                label="Middle Name"
                                                placeholder="Enter Value"
                                                id="middle_name"
                                                type="text"
                                                value={formValues?.general.middle_name}
                                                onChange={(e: any) => {
                                                    const value = e.target.value
                                                    setFormValues((prevVal: any) => ({
                                                        ...prevVal,
                                                        general: {
                                                            ...prevVal.general,
                                                            middle_name: value
                                                        },
                                                    }));
                                                }}
                                                />
                                            </div>
                                            <div className="flex gap-5">
                                                <InputField
                                                variant="auth"
                                                extra="mb-2 w-full"
                                                label="Last Name *"
                                                placeholder="Enter Value"
                                                id="last_name"
                                                type="text"
                                                value={formValues?.general.last_name}
                                                onChange={(e: any) => {
                                                    const value = e.target.value
                                                    setFormValues((prevVal: any) => ({
                                                        ...prevVal,
                                                        general: {
                                                            ...prevVal.general,
                                                            last_name: value
                                                        },
                                                    }));
                                                    setFormValuesErr((prevErr: any) => {
                                                        const newErrors = { ...prevErr };
                                                        if (!newErrors.general) {
                                                            newErrors.general = {};
                                                        }
                                                        if (!newErrors.general.last_name) {
                                                            newErrors.general.last_name = {};
                                                        }
                                                        if (!value) {
                                                            newErrors.general.last_name = "The field is required";
                                                        } else {
                                                            delete newErrors.general.last_name;
                                                            if (Object.keys(newErrors.general).length === 0) {
                                                                delete newErrors.general;
                                                            }
                                                        }
                                                        return newErrors;
                                                    });
                                                }}
                                                errMessage={formValuesErr?.general?.last_name || ""}
                                                />
                                                <InputField
                                                variant="auth"
                                                extra="mb-2 w-full"
                                                label="Email *"
                                                placeholder="Enter Value"
                                                id="email"
                                                type="text"
                                                value={formValues?.general.email}
                                                onChange={(e: any) => {
                                                    const value = e.target.value
                                                    setFormValues((prevVal: any) => ({
                                                        ...prevVal,
                                                        general: {
                                                            ...prevVal.general,
                                                            email: value
                                                        },
                                                    }));
                                                    setFormValuesErr((prevErr: any) => {
                                                        const newErrors = { ...prevErr };
                                                        if (!newErrors.general) {
                                                            newErrors.general = {};
                                                        }
                                                        if (!newErrors.general.email) {
                                                            newErrors.general.email = {};
                                                        }
                                                        if (!value) {
                                                            newErrors.general.email = "The field is required";
                                                        } else {
                                                            // delete newErrors.general.email;
                                                            // if (Object.keys(newErrors.general).length === 0) {
                                                            //     delete newErrors.general;
                                                            // }
                                                            // const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                                                            const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                                                                if (!emailRegex.test(value)) {
                                                                    newErrors.general.email = "Invalid email format";
                                                                } else {
                                                                    delete newErrors.general.email;
                                                                    if (Object.keys(newErrors.general).length === 0) {
                                                                        delete newErrors.general;
                                                                    }
                                                                }
                                                        }
                                                        return newErrors;
                                                    });
                                                }}
                                                errMessage={formValuesErr?.general?.email || ""}
                                                />
                                            </div>
                                            <div className="flex gap-5">
                                                <InputField
                                                variant="auth"
                                                extra="mb-2 w-full"
                                                label="Phone *"
                                                placeholder="Enter Value"
                                                id="phone"
                                                type="text"
                                                value={formValues?.general.phone}
                                                onChange={(e: any) => {
                                                    const value = e.target.value
                                                    if (/^\d*$/.test(value)) {
                                                        setFormValues((prevVal: any) => ({
                                                            ...prevVal,
                                                            general: {
                                                                ...prevVal.general,
                                                                phone: value
                                                            },
                                                        }));
                                                        setFormValuesErr((prevErr: any) => {
                                                            const newErrors = { ...prevErr };
                                                            if (!newErrors.general) {
                                                                newErrors.general = {};
                                                            }
                                                            if (!newErrors.general.phone) {
                                                                newErrors.general.phone = {};
                                                            }
                                                            if (!value) {
                                                                newErrors.general.phone = "The field is required";
                                                            } else {
                                                                delete newErrors.general.phone;
                                                                if (Object.keys(newErrors.general).length === 0) {
                                                                    delete newErrors.general;
                                                                }
                                                            }
                                                            return newErrors;
                                                        });
                                                    }
                                                }}
                                                errMessage={formValuesErr?.general?.phone || ""}
                                                />
                                                <InputField
                                                variant="auth"
                                                extra="mb-2 w-full"
                                                label="City *"
                                                placeholder="Enter Value"
                                                id="city"
                                                type="text"
                                                value={formValues?.general.city}
                                                onChange={(e: any) => {
                                                    const value = e.target.value
                                                    setFormValues((prevVal: any) => ({
                                                        ...prevVal,
                                                        general: {
                                                            ...prevVal.general,
                                                            city: value
                                                        },
                                                    }));
                                                    setFormValuesErr((prevErr: any) => {
                                                        const newErrors = { ...prevErr };
                                                        if (!newErrors.general) {
                                                            newErrors.general = {};
                                                        }
                                                        if (!newErrors.general.city) {
                                                            newErrors.general.city = {};
                                                        }
                                                        if (!value) {
                                                            newErrors.general.city = "The field is required";
                                                        } else {
                                                            delete newErrors.general.city;
                                                            if (Object.keys(newErrors.general).length === 0) {
                                                                delete newErrors.general;
                                                            }
                                                        }
                                                        return newErrors;
                                                    });
                                                }}
                                                errMessage={formValuesErr?.general?.city || ""}
                                                />
                                            </div>
                                            <div className="flex gap-5">
                                                <InputField
                                                variant="auth"
                                                extra="mb-2 w-full"
                                                label="Address *"
                                                placeholder="Enter Value"
                                                id="address"
                                                type="text"
                                                value={formValues?.general.address}
                                                onChange={(e: any) => {
                                                    const value = e.target.value
                                                    setFormValues((prevVal: any) => ({
                                                        ...prevVal,
                                                        general: {
                                                            ...prevVal.general,
                                                            address: value
                                                        },
                                                    }));
                                                    setFormValuesErr((prevErr: any) => {
                                                        const newErrors = { ...prevErr };
                                                        if (!newErrors.general) {
                                                            newErrors.general = {};
                                                        }
                                                        if (!newErrors.general.address) {
                                                            newErrors.general.address = {};
                                                        }
                                                        if (!value) {
                                                            newErrors.general.address = "The field is required";
                                                        } else {
                                                            delete newErrors.general.address;
                                                            if (Object.keys(newErrors.general).length === 0) {
                                                                delete newErrors.general;
                                                            }
                                                        }
                                                        return newErrors;
                                                    });
                                                }}
                                                errMessage={formValuesErr?.general?.address || ""}
                                                />
                                                <InputField
                                                variant="auth"
                                                extra="mb-2 w-full"
                                                label="Postal Code *"
                                                placeholder="Enter Value"
                                                id="postal_code"
                                                type="text"
                                                value={formValues?.general.postal_code}
                                                onChange={(e: any) => {
                                                    const value = e.target.value
                                                    setFormValues((prevVal: any) => ({
                                                        ...prevVal,
                                                        general: {
                                                            ...prevVal.general,
                                                            postal_code: value
                                                        },
                                                    }));
                                                    setFormValuesErr((prevErr: any) => {
                                                        const newErrors = { ...prevErr };
                                                        if (!newErrors.general) {
                                                            newErrors.general = {};
                                                        }
                                                        if (!newErrors.general.postal_code) {
                                                            newErrors.general.postal_code = {};
                                                        }
                                                        if (!value) {
                                                            newErrors.general.postal_code = "The field is required";
                                                        } else {
                                                            delete newErrors.general.postal_code;
                                                            if (Object.keys(newErrors.general).length === 0) {
                                                                delete newErrors.general;
                                                            }
                                                        }
                                                        return newErrors;
                                                    });
                                                }}
                                                errMessage={formValuesErr?.general?.postal_code || ""}
                                                />
                                            </div>
                                            <div className="flex gap-5">
                                                <InputField
                                                variant="auth"
                                                extra="mb-2 w-full"
                                                label="State *"
                                                placeholder="Enter Value"
                                                id="state"
                                                type="text"
                                                value={formValues?.general.state}
                                                onChange={(e: any) => {
                                                    const value = e.target.value
                                                    setFormValues((prevVal: any) => ({
                                                        ...prevVal,
                                                        general: {
                                                            ...prevVal.general,
                                                            state: value
                                                        },
                                                    }));
                                                    setFormValuesErr((prevErr: any) => {
                                                        const newErrors = { ...prevErr };
                                                        if (!newErrors.general) {
                                                            newErrors.general = {};
                                                        }
                                                        if (!newErrors.general.state) {
                                                            newErrors.general.state = {};
                                                        }
                                                        if (!value) {
                                                            newErrors.general.state = "The field is required";
                                                        } else {
                                                            delete newErrors.general.state;
                                                            if (Object.keys(newErrors.general).length === 0) {
                                                                delete newErrors.general;
                                                            }
                                                        }
                                                        return newErrors;
                                                    });
                                                }}
                                                errMessage={formValuesErr?.general?.state || ""}
                                                />
                                                <InputField
                                                variant="auth"
                                                extra="mb-2 w-full"
                                                label="Country *"
                                                placeholder="Enter Value"
                                                id="country"
                                                type="text"
                                                value={formValues?.general.country}
                                                onChange={(e: any) => {
                                                    const value = e.target.value
                                                    setFormValues((prevVal: any) => ({
                                                        ...prevVal,
                                                        general: {
                                                            ...prevVal.general,
                                                            country: value
                                                        },
                                                    }));
                                                    setFormValuesErr((prevErr: any) => {
                                                        const newErrors = { ...prevErr };
                                                        if (!newErrors.general) {
                                                            newErrors.general = {};
                                                        }
                                                        if (!newErrors.general.country) {
                                                            newErrors.general.country = {};
                                                        }
                                                        if (!value) {
                                                            newErrors.general.country = "The field is required";
                                                        } else {
                                                            delete newErrors.general.country;
                                                            if (Object.keys(newErrors.general).length === 0) {
                                                                delete newErrors.general;
                                                            }
                                                        }
                                                        return newErrors;
                                                    });
                                                }}
                                                errMessage={formValuesErr?.general?.country || ""}
                                                />
                                            </div>
                                            <div className="flex gap-5">
                                                <div className="mb-2 w-full">
                                                    <label className="text-sm text-gray-600  dark:text-white ml-1.5 font-bold text-sm font-bold text-gray-900 dark:text-white">
                                                        Birth Date
                                                    </label>
                                                    <input 
                                                        className="bg-gray/0 mt-2 flex h-12 w-full rounded-xl border p-3 text-sm text-gray-700 outline-none"
                                                        type="date" 
                                                        max={moment().format("YYYY-MM-DD")}
                                                        onKeyDown={(e) => e.preventDefault()} 
                                                        onPaste={(e) => e.preventDefault()}
                                                        onFocus={(e) => e.target.blur()}
                                                        value={formValues.general.birth_date ? moment.utc(formValues.general.birth_date).format("YYYY-MM-DD") : ""} 
                                                        onChange={(e) => {
                                                            const value = e.target.value ? moment.utc(e.target.value).format("YYYY-MM-DD") : null;
                                                            setFormValues((prevVal: any) => ({
                                                                ...prevVal,
                                                                general: {
                                                                    ...prevVal.general,
                                                                    birth_date: value
                                                                },
                                                            }));
                                                        }}
                                                    />
                                                </div>
                                                <div className="mb-2 w-full">        
                                                    <InputField
                                                    variant="auth"
                                                    extra="mb-2 w-full"
                                                    label="Reference ID"
                                                    placeholder="Enter Value"
                                                    id="reference_id"
                                                    type="text"
                                                    value={formValues?.general.reference_id}
                                                    onChange={(e: any) => {
                                                        const value = e.target.value
                                                        setFormValues((prevVal: any) => ({
                                                            ...prevVal,
                                                            general: {
                                                                ...prevVal.general,
                                                                reference_id: value
                                                            },
                                                        }));
                                                    }}
                                                    />
                                                </div>
                                            </div>
                                    </div>
                                )}
                                {step === 3 && (
                                    <div className="animate-fadeIn">
                                        <h2 className="text-xl font-semibold mb-4 text-gray-800">KYC Information</h2>
                                        {formValues.kyc.map((item: any, index: number) => (
                                            <div
                                                key={item.fileKey}
                                                className="border border-gray-200 rounded-2xl p-5 mb-6 shadow-sm relative"
                                            >
                                                {formValues.kyc.length > 1 && (
                                                    <button
                                                        onClick={() => removeKyc(index)}
                                                        className="absolute top-3 right-3 text-red-500 text-sm hover:underline"
                                                    >
                                                        Remove
                                                    </button>
                                                )}
                                                <div className="flex gap-5">
                                                    <InputField
                                                        variant="auth"
                                                        extra="mb-2 w-full"
                                                        label="Identification Number *"
                                                        placeholder="Enter Value"
                                                        id={`identification_number_${index}`}
                                                        type="text"
                                                        value={item.identification_number}
                                                        onChange={(e: any) => {
                                                            const value = e.target.value;
                                                            setFormValues((prev: any) => {
                                                                const updated = [...prev.kyc];
                                                                updated[index].identification_number = value;
                                                                return { ...prev, kyc: updated };
                                                            });
                                                            setFormValuesErr((prevErr: any) => {
                                                                const updatedErr = { ...prevErr };
                                                                if (!updatedErr.kyc) updatedErr.kyc = [];
                                                                if (!updatedErr.kyc[index]) updatedErr.kyc[index] = {};
                                                                if (!value.trim()) {
                                                                    updatedErr.kyc[index].identification_number = "The field is required";
                                                                } else {
                                                                    delete updatedErr.kyc[index].identification_number;
                                                                }
                                                                return updatedErr;
                                                            });
                                                        }}
                                                        errMessage={formValuesErr?.kyc?.[index]?.identification_number || ""}
                                                    />
                                                    <InputField
                                                        variant="auth"
                                                        extra="mb-2 w-full"
                                                        label="Identification Type *"
                                                        placeholder="Enter Value"
                                                        id={`identification_type_${index}`}
                                                        type="text"
                                                        value={item.identification_type}
                                                        onChange={(e: any) => {
                                                            const value = e.target.value;
                                                            setFormValues((prev: any) => {
                                                                const updated = [...prev.kyc];
                                                                updated[index].identification_type = value;
                                                                return { ...prev, kyc: updated };
                                                            });
                                                            setFormValuesErr((prevErr: any) => {
                                                                const updatedErr = { ...prevErr };
                                                                if (!updatedErr.kyc) updatedErr.kyc = [];
                                                                if (!updatedErr.kyc[index]) updatedErr.kyc[index] = {};
                                                                if (!value.trim()) {
                                                                    updatedErr.kyc[index].identification_type = "The field is required";
                                                                } else {
                                                                    delete updatedErr.kyc[index].identification_type;
                                                                }
                                                                return updatedErr;
                                                            });
                                                        }}
                                                        errMessage={formValuesErr?.kyc?.[index]?.identification_type || ""}
                                                    />
                                                </div>
                                                <div className="flex gap-5">
                                                    <div className="mb-2 w-full">
                                                        <label className="text-sm text-gray-600  dark:text-white ml-1.5 font-bold text-sm font-bold text-gray-900 dark:text-white">
                                                            Identification Issued At
                                                        </label>
                                                        <input
                                                            className={`bg-gray/0 mt-2 flex h-12 w-full rounded-xl border p-3 text-sm outline-none
                                                                ${formValuesErr?.kyc?.[index]?.identification_issued_at ? "border-gray-300" : "border-gray-300"}
                                                            `}
                                                            type="date"
                                                            value={
                                                                item.identification_issued_at
                                                                    ? moment.utc(item.identification_issued_at).format("YYYY-MM-DD")
                                                                    : ""
                                                            }
                                                            onChange={(e) => {
                                                                const value = e.target.value
                                                                    ? moment.utc(e.target.value).format("YYYY-MM-DD")
                                                                    : "";
                                                                setFormValues((prev: any) => {
                                                                    const updated = [...prev.kyc];
                                                                    updated[index].identification_issued_at = value;
                                                                    if (!value) {
                                                                        updated[index].identification_expired_at = "";
                                                                    }
                                                                    return { ...prev, kyc: updated };
                                                                });
                                                                setFormValuesErr((prevErr: any) => {
                                                                    const updatedErr = { ...prevErr };
                                                                    if (!updatedErr.kyc) updatedErr.kyc = [];
                                                                    if (!updatedErr.kyc[index]) updatedErr.kyc[index] = {};
                                                                    if (!value) {
                                                                        delete updatedErr.kyc[index].identification_issued_at;
                                                                        delete updatedErr.kyc[index].identification_expired_at;
                                                                    } else {
                                                                        delete updatedErr.kyc[index].identification_issued_at;
                                                                    }
                                                                    return updatedErr;
                                                                });
                                                            }}
                                                        />
                                                        {formValuesErr?.kyc?.[index]?.identification_issued_at && (
                                                            <p className="text-sm text-orange-500">
                                                                {formValuesErr.kyc[index].identification_issued_at}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <div className="mb-2 w-full">
                                                        <label className="text-sm text-gray-600  dark:text-white ml-1.5 font-bold text-sm font-bold text-gray-900 dark:text-white">
                                                            Identification Expired At
                                                        </label>
                                                        <input
                                                            className={`bg-gray/0 mt-2 flex h-12 w-full rounded-xl border p-3 text-sm outline-none
                                                                ${formValuesErr?.kyc?.[index]?.identification_expired_at ? "border-gray-300" : "border-gray-300"}
                                                            `}
                                                            type="date"
                                                            disabled={!item.identification_issued_at}
                                                            min={
                                                                item.identification_issued_at
                                                                    ? moment.utc(item.identification_issued_at).add(1, "day").format("YYYY-MM-DD")
                                                                    : undefined
                                                            }
                                                            value={
                                                                item.identification_expired_at
                                                                    ? moment.utc(item.identification_expired_at).format("YYYY-MM-DD")
                                                                    : ""
                                                            }
                                                            onChange={(e) => {
                                                                const value = e.target.value
                                                                    ? moment.utc(e.target.value).format("YYYY-MM-DD")
                                                                    : "";
                                                                setFormValues((prev: any) => {
                                                                    const updated = [...prev.kyc];
                                                                    updated[index].identification_expired_at = value;
                                                                    return { ...prev, kyc: updated };
                                                                });
                                                                setFormValuesErr((prevErr: any) => {
                                                                    const updatedErr = { ...prevErr };
                                                                    if (!updatedErr.kyc) updatedErr.kyc = [];
                                                                    if (!updatedErr.kyc[index]) updatedErr.kyc[index] = {};

                                                                    if (!value && item.identification_issued_at) {
                                                                        updatedErr.kyc[index].identification_expired_at = "The field is required";
                                                                    } else {
                                                                        delete updatedErr.kyc[index].identification_expired_at;
                                                                    }
                                                                    return updatedErr;
                                                                });
                                                            }}
                                                        />
                                                        {formValuesErr?.kyc?.[index]?.identification_expired_at && (
                                                            <p className="text-sm text-orange-500">
                                                                {formValuesErr.kyc[index].identification_expired_at}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex gap-5">
                                                    <div className="mb-2 w-full">
                                                        <label className="text-sm ml-1.5 font-bold text-gray-900 dark:text-white">
                                                            Document File *
                                                        </label>
                                                        <input
                                                            ref={(el) => (fileRefs.current[item.fileKey] = el)}
                                                            type="file"
                                                            accept="image/*,.pdf"
                                                            className="hidden"
                                                            onChange={(e: any) => handleFileUpload(e,item.fileKey, index)}
                                                        />
                                                        <div
                                                            onClick={() => fileRefs.current[item.fileKey]?.click()}
                                                            className="mt-2 h-32 w-full rounded-xl border border-gray-300 flex 
                                                                    flex-col items-center justify-center cursor-pointer bg-gray-50 hover:bg-gray-100 transition"
                                                        >
                                                            {!item.document_file && (
                                                                <div className="flex flex-col items-center text-gray-500">
                                                                    📁
                                                                    <p className="text-sm mt-1">Click to upload PDF / Image</p>
                                                                </div>
                                                            )}
                                                            {item.document_file && item.document_file_type === "image" && (
                                                                <img
                                                                    src={item.document_file}
                                                                    className="h-full object-contain rounded-xl"
                                                                    alt="Preview"
                                                                />
                                                            )}
                                                            {item.document_file && item.document_file_type === "pdf" && (
                                                                <div className="flex flex-col items-center text-red-600">
                                                                    📄
                                                                    <p className="text-xs mt-1">{item.document_file_name}</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                        {formValuesErr?.kyc?.[index]?.document_file && (
                                                            <p className="text-sm text-orange-500 mt-1">
                                                                {formValuesErr.kyc[index].document_file}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                        <div className="flex justify-end">
                                            <Button
                                                onClick={addKyc}
                                                className="rounded-xl px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white mt-2"
                                            >
                                                + Add Another KYC
                                            </Button>
                                        </div>
                                    </div>
                                )}
                                {step === 4 && (
                                    <div className="animate-fadeIn">
                                        <h2 className="text-xl font-semibold mb-4 text-gray-800">Payment Methods</h2>
                                        <div className="flex gap-5 mb-2">
                                            <div className="mb-2 w-full">
                                                <div className="mt-2 flex">
                                                    <div className="ml-2 flex items-center">
                                                        <input
                                                        type="radio"
                                                        className="h-4 w-4"
                                                        id="card"
                                                        name="algo"
                                                        value="card"
                                                        //@ts-ignore
                                                        checked={formValues.payment_method.type == "card"}
                                                        onChange={(e) => {
                                                            setFormValues((prevVal: any) => ({
                                                                ...prevVal,
                                                                payment_method: {
                                                                    ...prevVal.payment_method,
                                                                    type: e.target.value
                                                                },
                                                            }));
                                                        }}
                                                        />
                                                        <label
                                                        htmlFor="card"
                                                        className="mx-2 items-center"
                                                        >
                                                        Card
                                                        </label>
                                                    </div>
                                                    <div className="ml-2 flex items-center">
                                                        <input
                                                        type="radio"
                                                        className="h-4 w-4"
                                                        id="bank"
                                                        name="algo"
                                                        value="bank"
                                                        //@ts-ignore
                                                        checked={formValues.payment_method.type == "bank"}
                                                        onChange={(e) => {
                                                            setFormValues((prevVal: any) => ({
                                                                ...prevVal,
                                                                payment_method: {
                                                                    ...prevVal.payment_method,
                                                                    type: e.target.value
                                                                },
                                                            }));
                                                        }}
                                                        />
                                                        <label
                                                        htmlFor="bank"
                                                        className="mx-2 items-center"
                                                        >
                                                        Bank
                                                        </label>
                                                    </div>
                                                    <div className="ml-2 flex items-center">
                                                        <input
                                                        type="radio"
                                                        className="h-4 w-4"
                                                        id="crypto"
                                                        name="algo"
                                                        value="crypto"
                                                        //@ts-ignore
                                                        checked={formValues.payment_method.type == "crypto"}
                                                        onChange={(e) => {
                                                            setFormValues((prevVal: any) => ({
                                                                ...prevVal,
                                                                payment_method: {
                                                                    ...prevVal.payment_method,
                                                                    type: e.target.value
                                                                },
                                                            }));
                                                        }}
                                                        />
                                                        <label
                                                        htmlFor="crypto"
                                                        className="mx-2 items-center"
                                                        >
                                                        Crypto
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        {formValues.payment_method.type === "card" ? (
                                            <>
                                                {formValues.payment_method.card.map((cardItem: ICard, index: number) => (
                                                    <div 
                                                        key={index}
                                                        className="border border-gray-200 rounded-2xl p-5 mb-6 shadow-sm relative"
                                                    >
                                                        {formValues.payment_method.card.length > 1 && (
                                                            <button
                                                                onClick={() => {
                                                                    setFormValues((prev: any) => ({
                                                                        ...prev,
                                                                        payment_method: {
                                                                            ...prev.payment_method,
                                                                            card: prev.payment_method.card.filter((_: any, i: number) => i !== index)
                                                                        }
                                                                    }));
                                                                    setFormValuesErr((prevErr: any) => {
                                                                        const updatedErr = { ...prevErr };
                                                                        if (updatedErr.payment_method?.card?.[index]) {
                                                                            updatedErr.payment_method.card.splice(index, 1);
                                                                        }
                                                                        return updatedErr;
                                                                    });
                                                                }}
                                                                className="absolute top-3 right-3 text-red-500 text-sm hover:underline"
                                                            >
                                                                Remove
                                                            </button>
                                                        )}
                                                        <div className="flex gap-5">
                                                            <InputField
                                                                variant="auth"
                                                                extra="mb-2 w-full"
                                                                label="Card Holder Name *"
                                                                placeholder="Enter Value"
                                                                id={`card_holder_name_${index}`}
                                                                type="text"
                                                                value={cardItem.cardholder_name}
                                                                onChange={(e: any) => {
                                                                    const value = e.target.value;
                                                                    setFormValues((prev: any) => {
                                                                        const updated = [...prev.payment_method.card];
                                                                        updated[index].cardholder_name = value;
                                                                        return {
                                                                            ...prev,
                                                                            payment_method: {
                                                                                ...prev.payment_method,
                                                                                card: updated,
                                                                            },
                                                                        };
                                                                    });
                                                                    setFormValuesErr((prevErr: any) => {
                                                                        const updatedErr = { ...prevErr };
                                                                        if (!updatedErr.payment_method) updatedErr.payment_method = {};
                                                                        if (!updatedErr.payment_method.card) updatedErr.payment_method.card = [];
                                                                        if (!updatedErr.payment_method.card[index]) updatedErr.payment_method.card[index] = {};

                                                                        if (!value.trim()) {
                                                                            updatedErr.payment_method.card[index].cardholder_name = "The field is required";
                                                                        } else {
                                                                            delete updatedErr.payment_method.card[index].cardholder_name;
                                                                        }
                                                                        return updatedErr;
                                                                    });
                                                                }}
                                                                errMessage={formValuesErr?.payment_method?.card?.[index]?.cardholder_name || ""}
                                                            />
                                                            <InputField
                                                                variant="auth"
                                                                extra="mb-2 w-full"
                                                                label="Card Number *"
                                                                placeholder="Enter Value"
                                                                id={`card_number_${index}`}
                                                                type="text"
                                                                value={cardItem.card_number}
                                                                onChange={(e: any) => {
                                                                    const value = e.target.value;
                                                                    if (!/^\d*$/.test(value)) return;
                                                                    setFormValues((prev: any) => {
                                                                        const updated = [...prev.payment_method.card];
                                                                        updated[index].card_number = value;
                                                                        return {
                                                                            ...prev,
                                                                            payment_method: {
                                                                                ...prev.payment_method,
                                                                                card: updated,
                                                                            },
                                                                        };
                                                                    });
                                                                    setFormValuesErr((prevErr: any) => {
                                                                        const updatedErr = { ...prevErr };
                                                                        if (!updatedErr.payment_method) updatedErr.payment_method = {};
                                                                        if (!updatedErr.payment_method.card) updatedErr.payment_method.card = [];
                                                                        if (!updatedErr.payment_method.card[index]) updatedErr.payment_method.card[index] = {};

                                                                        if (!value.trim()) {
                                                                            updatedErr.payment_method.card[index].card_number = "The field is required";
                                                                        } else {
                                                                            delete updatedErr.payment_method.card[index].card_number;
                                                                        }
                                                                        return updatedErr;
                                                                    });
                                                                }}
                                                                errMessage={formValuesErr?.payment_method?.card?.[index]?.card_number || ""}
                                                            />
                                                        </div>
                                                        <div className="flex gap-5">
                                                            <InputField
                                                                variant="auth"
                                                                extra="mb-2 w-full"
                                                                label="Expiration Month *"
                                                                placeholder="MM"
                                                                id={`expiration_month_${index}`}
                                                                type="text"
                                                                value={cardItem.expiration_month}
                                                                onChange={(e: any) => {
                                                                    const value = e.target.value;
                                                                    if (!/^\d*$/.test(value) || value.length > 2) return;
                                                                    setFormValues((prev: any) => {
                                                                        const updated = [...prev.payment_method.card];
                                                                        updated[index].expiration_month = value;
                                                                        return {
                                                                            ...prev,
                                                                            payment_method: {
                                                                                ...prev.payment_method,
                                                                                card: updated,
                                                                            },
                                                                        };
                                                                    });
                                                                    setFormValuesErr((prevErr: any) => {
                                                                        const updatedErr = { ...prevErr };
                                                                        if (!updatedErr.payment_method) updatedErr.payment_method = {};
                                                                        if (!updatedErr.payment_method.card) updatedErr.payment_method.card = [];
                                                                        if (!updatedErr.payment_method.card[index]) updatedErr.payment_method.card[index] = {};
                                                                        if (!value.trim()) {
                                                                            updatedErr.payment_method.card[index].expiration_month = "The field is required";
                                                                        } else {
                                                                            delete updatedErr.payment_method.card[index].expiration_month;
                                                                        }
                                                                        return updatedErr;
                                                                    });
                                                                }}
                                                                errMessage={formValuesErr?.payment_method?.card?.[index]?.expiration_month || ""}
                                                            />
                                                            <InputField
                                                                variant="auth"
                                                                extra="mb-2 w-full"
                                                                label="Expiration Year *"
                                                                placeholder="YYYY"
                                                                id={`expiration_year_${index}`}
                                                                type="text"
                                                                value={cardItem.expiration_year}
                                                                onChange={(e: any) => {
                                                                    const value = e.target.value;
                                                                    if (!/^\d*$/.test(value) || value.length > 4) return;
                                                                    setFormValues((prev: any) => {
                                                                        const updated = [...prev.payment_method.card];
                                                                        updated[index].expiration_year = value;
                                                                        return {
                                                                            ...prev,
                                                                            payment_method: {
                                                                                ...prev.payment_method,
                                                                                card: updated,
                                                                            },
                                                                        };
                                                                    });
                                                                    setFormValuesErr((prevErr: any) => {
                                                                        const updatedErr = { ...prevErr };
                                                                        if (!updatedErr.payment_method) updatedErr.payment_method = {};
                                                                        if (!updatedErr.payment_method.card) updatedErr.payment_method.card = [];
                                                                        if (!updatedErr.payment_method.card[index]) updatedErr.payment_method.card[index] = {};
                                                                        if (!value.trim()) {
                                                                            updatedErr.payment_method.card[index].expiration_year = "The field is required";
                                                                        } else {
                                                                            delete updatedErr.payment_method.card[index].expiration_year;
                                                                        }
                                                                        return updatedErr;
                                                                    });
                                                                }}
                                                                errMessage={formValuesErr?.payment_method?.card?.[index]?.expiration_year || ""}
                                                            />
                                                        </div>
                                                        <div className="flex gap-5">
                                                            <InputField
                                                                variant="auth"
                                                                extra="mb-2 w-full"
                                                                label="CVV *"
                                                                placeholder="123"
                                                                id={`cvv_${index}`}
                                                                type="text"
                                                                value={cardItem.cvv}
                                                                onChange={(e: any) => {
                                                                    const value = e.target.value;
                                                                    if (!/^\d*$/.test(value) || value.length > 3) return;
                                                                    setFormValues((prev: any) => {
                                                                        const updated = [...prev.payment_method.card];
                                                                        updated[index].cvv = value;
                                                                        return {
                                                                            ...prev,
                                                                            payment_method: {
                                                                                ...prev.payment_method,
                                                                                card: updated,
                                                                            },
                                                                        };
                                                                    });
                                                                    setFormValuesErr((prevErr: any) => {
                                                                        const updatedErr = { ...prevErr };
                                                                        if (!updatedErr.payment_method) updatedErr.payment_method = {};
                                                                        if (!updatedErr.payment_method.card) updatedErr.payment_method.card = [];
                                                                        if (!updatedErr.payment_method.card[index]) updatedErr.payment_method.card[index] = {};
                                                                        if (!value.trim()) {
                                                                            updatedErr.payment_method.card[index].cvv = "The field is required";
                                                                        } else {
                                                                            delete updatedErr.payment_method.card[index].cvv;
                                                                        }
                                                                        return updatedErr;
                                                                    });
                                                                }}
                                                                errMessage={formValuesErr?.payment_method?.card?.[index]?.cvv || ""}
                                                            />
                                                        </div>
                                                    </div>
                                                ))}
                                                <div className="flex justify-end">
                                                    <Button
                                                        onClick={() => {
                                                            setFormValues((prev: any) => ({
                                                                ...prev,
                                                                payment_method: {
                                                                    ...prev.payment_method,
                                                                    card: [
                                                                        ...prev.payment_method.card,
                                                                        {
                                                                            cardholder_name: "",
                                                                            card_number: "",
                                                                            expiration_month: "",
                                                                            expiration_year: "",
                                                                            cvv: "",
                                                                        },
                                                                    ],
                                                                },
                                                            }));
                                                        }}
                                                        className="rounded-xl px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white mt-2"
                                                    >
                                                        + Add Another Card
                                                    </Button>
                                                </div>
                                            </>
                                        ) : formValues.payment_method.type === "bank" ? (
                                            <>
                                                {formValues.payment_method.bank.map((bankItem: IBank, index: number) => (
                                                    <div
                                                        key={index}
                                                        className="border border-gray-200 rounded-2xl p-5 mb-6 shadow-sm relative"
                                                    >
                                                        {formValues.payment_method.bank.length > 1 && (
                                                            <button
                                                                onClick={() => {
                                                                    setFormValues((prev: any) => ({
                                                                        ...prev,
                                                                        payment_method: {
                                                                            ...prev.payment_method,
                                                                            bank: prev.payment_method.bank.filter(
                                                                                (_: any, i: number) => i !== index
                                                                            ),
                                                                        },
                                                                    }));
                                                                    setFormValuesErr((prevErr: any) => {
                                                                        const updatedErr = { ...prevErr };
                                                                        if (updatedErr.payment_method?.bank?.[index]) {
                                                                            updatedErr.payment_method.bank.splice(index, 1);
                                                                        }
                                                                        return updatedErr;
                                                                    });
                                                                }}
                                                                className="absolute top-3 right-3 text-red-500 text-sm hover:underline"
                                                            >
                                                                Remove
                                                            </button>
                                                        )}
                                                        <div className="flex gap-5">
                                                            <InputField
                                                                variant="auth"
                                                                extra="mb-2 w-full"
                                                                label="IBAN *"
                                                                placeholder="Enter Value"
                                                                id={`iban_${index}`}
                                                                type="text"
                                                                value={bankItem.iban}
                                                                onChange={(e: any) => {
                                                                    const value = e.target.value;
                                                                    setFormValues((prev: any) => {
                                                                        const updated = [...prev.payment_method.bank];
                                                                        updated[index].iban = value;
                                                                        return {
                                                                            ...prev,
                                                                            payment_method: {
                                                                                ...prev.payment_method,
                                                                                bank: updated,
                                                                            },
                                                                        };
                                                                    });
                                                                    setFormValuesErr((prevErr: any) => {
                                                                        const updatedErr = { ...prevErr };
                                                                        if (!updatedErr.payment_method) updatedErr.payment_method = {};
                                                                        if (!updatedErr.payment_method.bank) updatedErr.payment_method.bank = [];
                                                                        if (!updatedErr.payment_method.bank[index])
                                                                            updatedErr.payment_method.bank[index] = {};
                                                                        if (!value.trim()) {
                                                                            updatedErr.payment_method.bank[index].iban =
                                                                                "The field is required";
                                                                        } else {
                                                                            delete updatedErr.payment_method.bank[index].iban;
                                                                        }
                                                                        return updatedErr;
                                                                    });
                                                                }}
                                                                errMessage={
                                                                    formValuesErr?.payment_method?.bank?.[index]?.iban || ""
                                                                }
                                                            />
                                                            <InputField
                                                                variant="auth"
                                                                extra="mb-2 w-full"
                                                                label="Swift No *"
                                                                placeholder="Enter Value"
                                                                id={`swift_no_${index}`}
                                                                type="text"
                                                                value={bankItem.swift_no}
                                                                onChange={(e: any) => {
                                                                    const value = e.target.value;
                                                                    setFormValues((prev: any) => {
                                                                        const updated = [...prev.payment_method.bank];
                                                                        updated[index].swift_no = value;
                                                                        return {
                                                                            ...prev,
                                                                            payment_method: {
                                                                                ...prev.payment_method,
                                                                                bank: updated,
                                                                            },
                                                                        };
                                                                    });
                                                                    setFormValuesErr((prevErr: any) => {
                                                                        const updatedErr = { ...prevErr };
                                                                        if (!updatedErr.payment_method) updatedErr.payment_method = {};
                                                                        if (!updatedErr.payment_method.bank) updatedErr.payment_method.bank = [];
                                                                        if (!updatedErr.payment_method.bank[index])
                                                                            updatedErr.payment_method.bank[index] = {};

                                                                        if (!value.trim()) {
                                                                            updatedErr.payment_method.bank[index].swift_no =
                                                                                "The field is required";
                                                                        } else {
                                                                            delete updatedErr.payment_method.bank[index].swift_no;
                                                                        }
                                                                        return updatedErr;
                                                                    });
                                                                }}
                                                                errMessage={
                                                                    formValuesErr?.payment_method?.bank?.[index]?.swift_no || ""
                                                                }
                                                            />
                                                        </div>
                                                        <div className="flex gap-5">
                                                            <InputField
                                                                variant="auth"
                                                                extra="mb-2 w-full"
                                                                label="Bank ID"
                                                                placeholder="Enter Value"
                                                                id={`bank_id_${index}`}
                                                                type="text"
                                                                value={bankItem.bank_id}
                                                                onChange={(e: any) => {
                                                                    const value = e.target.value;
                                                                    setFormValues((prev: any) => {
                                                                        const updated = [...prev.payment_method.bank];
                                                                        updated[index].bank_id = value;
                                                                        return {
                                                                            ...prev,
                                                                            payment_method: {
                                                                                ...prev.payment_method,
                                                                                bank: updated,
                                                                            },
                                                                        };
                                                                    });
                                                                }}
                                                                errMessage={""}
                                                            />
                                                            <InputField
                                                                variant="auth"
                                                                extra="mb-2 w-full"
                                                                label="Account Number"
                                                                placeholder="Enter Value"
                                                                id={`account_number_${index}`}
                                                                type="text"
                                                                value={bankItem.account_number}
                                                                onChange={(e: any) => {
                                                                    const value = e.target.value;
                                                                    setFormValues((prev: any) => {
                                                                        const updated = [...prev.payment_method.bank];
                                                                        updated[index].account_number = value;
                                                                        return {
                                                                            ...prev,
                                                                            payment_method: {
                                                                                ...prev.payment_method,
                                                                                bank: updated,
                                                                            },
                                                                        };
                                                                    });
                                                                }}
                                                                errMessage={""}
                                                            />
                                                        </div>
                                                        <div className="flex gap-5">
                                                            <InputField
                                                                variant="auth"
                                                                extra="mb-2 w-full"
                                                                label="Account Type"
                                                                placeholder="Enter Value"
                                                                id={`account_type_${index}`}
                                                                type="text"
                                                                value={bankItem.account_type}
                                                                onChange={(e: any) => {
                                                                    const value = e.target.value;
                                                                    setFormValues((prev: any) => {
                                                                        const updated = [...prev.payment_method.bank];
                                                                        updated[index].account_type = value;
                                                                        return {
                                                                            ...prev,
                                                                            payment_method: {
                                                                                ...prev.payment_method,
                                                                                bank: updated,
                                                                            },
                                                                        };
                                                                    });
                                                                }}
                                                                errMessage={""}
                                                            />
                                                            <InputField
                                                                variant="auth"
                                                                extra="mb-2 w-full"
                                                                label="Bank Name"
                                                                placeholder="Enter Value"
                                                                id={`bank_name_${index}`}
                                                                type="text"
                                                                value={bankItem.bank_name}
                                                                onChange={(e: any) => {
                                                                    const value = e.target.value;
                                                                    setFormValues((prev: any) => {
                                                                        const updated = [...prev.payment_method.bank];
                                                                        updated[index].bank_name = value;
                                                                        return {
                                                                            ...prev,
                                                                            payment_method: {
                                                                                ...prev.payment_method,
                                                                                bank: updated,
                                                                            },
                                                                        };
                                                                    });
                                                                }}
                                                                errMessage={""}
                                                            />
                                                        </div>
                                                        <div className="flex gap-5">
                                                            <InputField
                                                                variant="auth"
                                                                extra="mb-2 w-full"
                                                                label="Bank Branch"
                                                                placeholder="Enter Value"
                                                                id={`bank_branch_${index}`}
                                                                type="text"
                                                                value={bankItem.bank_branch}
                                                                onChange={(e: any) => {
                                                                    const value = e.target.value;
                                                                    setFormValues((prev: any) => {
                                                                        const updated = [...prev.payment_method.bank];
                                                                        updated[index].bank_branch = value;
                                                                        return {
                                                                            ...prev,
                                                                            payment_method: {
                                                                                ...prev.payment_method,
                                                                                bank: updated,
                                                                            },
                                                                        };
                                                                    });
                                                                }}
                                                                errMessage={""}
                                                            />
                                                        </div>
                                                    </div>
                                                ))}
                                                <div className="flex justify-end">
                                                    <button
                                                        onClick={() =>
                                                            setFormValues((prev: any) => ({
                                                                ...prev,
                                                                payment_method: {
                                                                    ...prev.payment_method,
                                                                    bank: [
                                                                        ...prev.payment_method.bank,
                                                                        {
                                                                            iban: "",
                                                                            swift_no: "",
                                                                            bank_id: "",
                                                                            account_number: "",
                                                                            account_type: "",
                                                                            bank_name: "",
                                                                            bank_branch: "",
                                                                        },
                                                                    ],
                                                                },
                                                            }))
                                                        }
                                                        className="rounded-xl px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white mt-3 float-end"
                                                    >
                                                        + Add Another Bank
                                                    </button>
                                                </div>
                                            </>
                                        ) : formValues.payment_method.type === "crypto" ? (
                                            <>
                                                {formValues.payment_method.crypto.map((cryptoItem: ICrypto, index: number) => (
                                                    <div
                                                        key={index}
                                                        className="border border-gray-200 rounded-2xl p-5 mb-6 shadow-sm relative"
                                                    >
                                                        {formValues.payment_method.crypto.length > 1 && (
                                                            <button
                                                                onClick={() => {
                                                                    setFormValues((prev: any) => ({
                                                                        ...prev,
                                                                        payment_method: {
                                                                            ...prev.payment_method,
                                                                            crypto: prev.payment_method.crypto.filter(
                                                                                (_: any, i: number) => i !== index
                                                                            ),
                                                                        },
                                                                    }));
                                                                    setFormValuesErr((prevErr: any) => {
                                                                        const updatedErr = { ...prevErr };
                                                                        if (updatedErr.payment_method?.crypto?.[index]) {
                                                                            updatedErr.payment_method.crypto.splice(index, 1);
                                                                        }
                                                                        return updatedErr;
                                                                    });
                                                                }}
                                                                className="absolute top-3 right-3 text-red-500 text-sm hover:underline"
                                                            >
                                                                Remove
                                                            </button>
                                                        )}
                                                        <div className="flex gap-5">
                                                            <InputField
                                                                variant="auth"
                                                                extra="mb-2 w-full"
                                                                label="Wallet Address *"
                                                                placeholder="Enter Value"
                                                                id={`wallet_address_${index}`}
                                                                type="text"
                                                                value={cryptoItem.wallet_address}
                                                                onChange={(e: any) => {
                                                                    const value = e.target.value;
                                                                    setFormValues((prev: any) => {
                                                                        const updated = [...prev.payment_method.crypto];
                                                                        updated[index].wallet_address = value;
                                                                        return {
                                                                            ...prev,
                                                                            payment_method: {
                                                                                ...prev.payment_method,
                                                                                crypto: updated,
                                                                            },
                                                                        };
                                                                    });
                                                                    setFormValuesErr((prevErr: any) => {
                                                                        const updatedErr = { ...prevErr };
                                                                        if (!updatedErr.payment_method)
                                                                            updatedErr.payment_method = {};
                                                                        if (!updatedErr.payment_method.crypto)
                                                                            updatedErr.payment_method.crypto = [];
                                                                        if (!updatedErr.payment_method.crypto[index])
                                                                            updatedErr.payment_method.crypto[index] = {};
                                                                        if (!value.trim()) {
                                                                            updatedErr.payment_method.crypto[index].wallet_address =
                                                                                "The field is required";
                                                                        } else {
                                                                            delete updatedErr.payment_method.crypto[index]
                                                                                .wallet_address;
                                                                        }
                                                                        return updatedErr;
                                                                    });
                                                                }}
                                                                errMessage={
                                                                    formValuesErr?.payment_method?.crypto?.[index]?.wallet_address || ""
                                                                }
                                                            />
                                                        </div>
                                                    </div>
                                                ))}
                                                <div className="flex justify-end">
                                                    <button
                                                        onClick={() =>
                                                            setFormValues((prev: any) => ({
                                                                ...prev,
                                                                payment_method: {
                                                                    ...prev.payment_method,
                                                                    crypto: [
                                                                        ...prev.payment_method.crypto,
                                                                        { wallet_address: "" },
                                                                    ],
                                                                },
                                                            }))
                                                        }
                                                        className="rounded-xl px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white mt-3 float-end"
                                                    >
                                                        + Add Another Wallet
                                                    </button>
                                                </div>
                                            </>
                                        ) :  null}
                                    </div>
                                )}
                                <div className="flex justify-between mt-10">
                                    <div>
                                        {step !== 1 && (
                                            <button
                                                onClick={prevStep}
                                                className="linear rounded-xl bg-red-50 px-5 py-2 text-base font-medium text-red-600 outline-none transition duration-200 hover:bg-red-600/5 active:bg-red-700/5 disabled:opacity-50 dark:bg-red-400/10 dark:text-white dark:hover:bg-red-300/10 dark:active:bg-red-200/10"
                                            >
                                                Previous
                                            </button>
                                        )}
                                    </div>
                                    <div>
                                        {step === 4 ? (
                                            <button
                                                onClick={handleSubmit}
                                                className="linear rounded-xl bg-indigo-600 px-5 py-2 text-white font-medium text-indigo-500 outline-none transition duration-200 dark:bg-indigo-400/10 dark:text-white dark:hover:bg-indigo-300/10 dark:active:bg-indigo-200/10"
                                            >
                                                {isLoading ? (
                                                    <DivLoader className="h-6 w-6 border-white" />
                                                ) : (
                                                    "Submit"
                                                )}
                                            </button>
                                        ) : (
                                            <button
                                                onClick={nextStep}
                                                className="linear rounded-xl bg-indigo-600 px-5 py-2 text-white font-medium text-indigo-500 outline-none transition duration-200 dark:bg-indigo-400/10 dark:text-white dark:hover:bg-indigo-300/10 dark:active:bg-indigo-200/10"
                                            >
                                                Next
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </Card>
                    </div>
                </div>
            </div>
        </>
    )
}

export default CustomerRegistrationForm;
